
// src/components/Header.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    ListItemIcon,
    Chip,
    IconButton,
    Menu,
    MenuItem,
    Avatar,
    Box,
    ListItemText,
    Tooltip,
    Typography,
    Button,
    CircularProgress,
    Divider,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BugReportIcon from '@mui/icons-material/BugReport';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PolicyIcon from '@mui/icons-material/Policy';
import GavelIcon from '@mui/icons-material/Gavel';
import LogoutIcon from '@mui/icons-material/Logout';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import { useChatbots } from '../contexts/ChatbotContext';
import { useTheme } from './../ThemeContext';
import { CUSTOMER_APPLICATION_URL } from "../config";
import PanelSwitcher from './PanelSwitcher';
import HeaderNotifications from './HeaderNotifications';
import HeaderReminders from './HeaderReminders';
import { usePanel, PanelType } from '../contexts/PanelContext';
import axiosInstance from '../utils/axiosInstance';

const STATUS_CONFIG = {
    online: { label: 'Online', color: '#22c55e' },
    busy: { label: 'Busy', color: '#ef4444' },
    away: { label: 'Away', color: '#f59e0b' },
    offline: { label: 'Offline', color: '#9ca3af' },
};

const HEADER_ACCENT_COLOR = "#1d4ed8";

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
};

// KlonIT Logo SVG
const KlonITLogo = ({ color = "#1e293b", size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
        <path d="M8 6L16 16L8 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 6L24 16L16 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
    </svg>
);

// Animated Chatbot Icon
const AnimatedChatbot = ({ themeColor = HEADER_ACCENT_COLOR, panelLabel = 'Creator Panel' }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
        }}
    >
        <Box
            sx={{
                position: 'relative',
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {/* Chatbot face */}
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 2px 8px ${themeColor}4d`,
                    position: 'relative',
                    animation: 'float 3s ease-in-out infinite',
                    '@keyframes float': {
                        '0%, 100%': {
                            transform: 'translateY(0)',
                        },
                        '50%': {
                            transform: 'translateY(-2px)',
                        },
                    },
                }}
            >
                {/* Eyes */}
                <Box sx={{ display: 'flex', gap: 0.5, position: 'absolute', top: 8 }}>
                    <Box
                        sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: '#fff',
                            animation: 'blink 3s ease-in-out infinite',
                            '@keyframes blink': {
                                '0%, 90%, 100%': { transform: 'scaleY(1)' },
                                '95%': { transform: 'scaleY(0.1)' },
                            },
                        }}
                    />
                    <Box
                        sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: '#fff',
                            animation: 'blink 3s ease-in-out infinite',
                        }}
                    />
                </Box>
                {/* Mouth/Smile */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 6,
                        width: 8,
                        height: 4,
                        borderRadius: '0 0 4px 4px',
                        bgcolor: '#fff',
                    }}
                />
                {/* Antenna */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -4,
                        width: 2,
                        height: 6,
                        bgcolor: themeColor,
                        borderRadius: '2px',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: -3,
                            left: -2,
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: themeColor,
                            animation: 'glow 1.5s ease-in-out infinite',
                            '@keyframes glow': {
                                '0%, 100%': {
                                    boxShadow: `0 0 4px ${themeColor}99`,
                                },
                                '50%': {
                                    boxShadow: `0 0 8px ${themeColor}`,
                                },
                            },
                        },
                    }}
                />
            </Box>
        </Box>
        {/* Text */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Typography
                sx={{
                    fontFamily,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: themeColor,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    lineHeight: 1,
                }}
            >
                KlonIT Powered
            </Typography>
            <Typography
                sx={{
                    fontFamily,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: colors.textPrimary,
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                {panelLabel}
                <Box
                    component="span"
                    sx={{
                        display: 'inline-block',
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: themeColor,
                        animation: 'statusPulse 2s ease-in-out infinite',
                        '@keyframes statusPulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                        },
                    }}
                />
            </Typography>
        </Box>
        {/* Animated Clone Scale Grow */}
        <Box
            sx={{
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'center',
                gap: 0.75,
                ml: 2,
                pl: 2,
                borderLeft: '1px solid rgba(0,0,0,0.08)',
            }}
        >
            {['Clone', 'Scale', 'Grow'].map((word, index) => (
                <Typography
                    key={word}
                    sx={{
                        fontFamily,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        color: themeColor,
                        animation: `fadeInOut 3s ease-in-out infinite`,
                        animationDelay: `${index * 1}s`,
                        opacity: 0.4,
                        '@keyframes fadeInOut': {
                            '0%, 100%': { opacity: 0.4, transform: 'scale(1)' },
                            '33%': { opacity: 1, transform: 'scale(1.1)' },
                            '66%': { opacity: 0.4, transform: 'scale(1)' },
                        },
                    }}
                >
                    {word}
                </Typography>
            ))}
        </Box>
    </Box>
);

export default function Header({ toggleSidebar, isMobile }) {
    const muiTheme = useMuiTheme();
    const isMobileScreen = useMediaQuery(muiTheme.breakpoints.down('md'));
    const {
        refreshChatbots,
        chatbots,
        activeChatbot,
        isLoading,
        switchActiveChatbot
    } = useChatbots();

    const { activePanel } = usePanel();
    const getPanelConfig = () => {
        switch (activePanel) {
            case PanelType.FINANCE:
                return { label: 'Finance Portal', color: '#22c55e' };
            case PanelType.TRADING:
                return { label: 'Trading Portal', color: '#f59e0b' };
            case PanelType.STUDENT:
                return { label: 'Student Portal', color: '#8b5cf6' };
            case PanelType.CUSTOMER:
                return { label: 'Customer Panel', color: '#10b981' };
            default:
                return { label: 'Creator Panel', color: HEADER_ACCENT_COLOR };
        }
    };
    const { label: currentPanelLabel, color: currentHeaderColor } = getPanelConfig();

    const [anchorEl, setAnchorEl] = useState(null);
    const [chatbotMenuAnchor, setChatbotMenuAnchor] = useState(null);
    const { mode, toggleTheme } = useTheme();
    const [customerPortalUrl, setCustomerPortalUrl] = useState('');
    const [fname, setFname] = useState('');
    const [lname, setLname] = useState('');
    const [profileImg, setProfileImg] = useState('');
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [teamMenuOpen, setTeamMenuOpen] = useState(null);
    const [userStatus, setUserStatus] = useState('busy');


    useEffect(() => {
        const loadUserData = (userData) => {
            setUser(userData);
            setProfileImg(userData?.profile?.profile_image || "");
            setFname(userData?.profile?.fname || "");
            setLname(userData?.profile?.lname || "");
            setUserStatus(userData?.active_team?.user_status || 'busy');
        };

        const userStr = localStorage.getItem("user");
        const parsedUser = userStr ? JSON.parse(userStr) : {};
        loadUserData(parsedUser);

        const customerToken = localStorage.getItem("customer_portal_token");
        if (customerToken) {
            setCustomerPortalUrl(
                `${CUSTOMER_APPLICATION_URL}/admin-authentication/${customerToken}`
            );
        }

        const handleProfileUpdate = (event) => {
            loadUserData(event.detail);
        };
        window.addEventListener('userLocalStorageUpdated', handleProfileUpdate);
        return () => window.removeEventListener('userLocalStorageUpdated', handleProfileUpdate);
    }, []);

    const handleTeamSwitch = (team) => {
        const updatedUser = { ...user, active_team: team };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        // Full page reload to reinitialize all contexts with new team data
        const switchedRole = team.user_role_in_team?.toLowerCase();
        if (switchedRole === 'student') {
            localStorage.setItem('active_panel', 'student');
            window.location.href = '/student-portal/dashboard';
        } else if (switchedRole === 'customer') {
            localStorage.setItem('active_panel', 'customer');
            window.location.href = '/customer-panel/dashboard';
        } else {
            localStorage.setItem('active_panel', 'creator');
            window.location.href = '/chatbots';
        }
    };

    const handleProfileClick = (event) => setAnchorEl(event.currentTarget);
    const handleProfileClose = () => setAnchorEl(null);
    const handleChatbotMenuOpen = (event) => setChatbotMenuAnchor(event.currentTarget);
    const handleChatbotMenuClose = () => setChatbotMenuAnchor(null);

    const handleChatbotSelect = (chatbotId) => {
        switchActiveChatbot(chatbotId, { navigateToDashboard: true });
        handleChatbotMenuClose();
    };

    const handleCreateNew = async () => {
        handleChatbotMenuClose();
        navigate(`chatbot/create`);
    };

    const handleStatusChange = async (newStatus) => {
        const previousStatus = userStatus;
        setUserStatus(newStatus);
        try {
            await axiosInstance.put('/api/users/status', { status: newStatus });
            const userStr = localStorage.getItem('user');
            if (userStr) {
                const userData = JSON.parse(userStr);
                if (userData.active_team) {
                    userData.active_team.user_status = newStatus;
                }
                localStorage.setItem('user', JSON.stringify(userData));
            }
        } catch (err) {
            console.error('Failed to update status:', err);
            setUserStatus(previousStatus);
        }
    };

    return (
        <AppBar
            position="fixed"
            elevation={0}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: 'rgba(255,255,255,0.85)',
                backdropFilter: 'blur(12px)',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}
        >
            <Toolbar sx={{ gap: { xs: 0.5, md: 1 }, px: { xs: 1, sm: 2 }, minHeight: { xs: 56, sm: 64 } }}>
                {/* Mobile Hamburger Menu */}
                <IconButton
                    onClick={toggleSidebar}
                    sx={{
                        display: { xs: 'flex', md: 'none' },
                        color: currentHeaderColor,
                        mr: 0.5,
                    }}
                >
                    <MenuIcon />
                </IconButton>

                {/* Logo & Animated Chatbot */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AnimatedChatbot themeColor={currentHeaderColor} panelLabel={currentPanelLabel} />
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                {/* Team Switcher */}
                {user && (
                    <Box sx={{ mr: { xs: 0, md: 1 } }}>
                        {/* Mobile: icon-only button */}
                        <Tooltip title={user.active_team?.team_name || "Select Team"}>
                            <IconButton
                                onClick={(e) => setTeamMenuOpen(e.currentTarget)}
                                sx={{
                                    display: { xs: 'flex', md: 'none' },
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    bgcolor: `${currentHeaderColor}14`,
                                    border: `1px solid ${currentHeaderColor}26`,
                                    '&:hover': { bgcolor: `${currentHeaderColor}1f` },
                                }}
                            >
                                <GroupsOutlinedIcon sx={{ fontSize: '1.1rem', color: currentHeaderColor }} />
                            </IconButton>
                        </Tooltip>
                        {/* Desktop: full button */}
                        <Button
                            onClick={(e) => setTeamMenuOpen(e.currentTarget)}
                            startIcon={
                                <Box sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '8px',
                                    background: `linear-gradient(135deg, ${currentHeaderColor}, ${currentHeaderColor}dd)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: `0 2px 6px ${currentHeaderColor}4d`,
                                }}>
                                    <GroupsOutlinedIcon sx={{ fontSize: '1rem', color: '#fff' }} />
                                </Box>
                            }
                            endIcon={
                                <ExpandMoreIcon
                                    sx={{
                                        fontSize: '1rem',
                                        transition: 'transform 0.2s ease',
                                        transform: teamMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                />
                            }
                            sx={{
                                display: { xs: 'none', md: 'inline-flex' },
                                fontFamily,
                                fontSize: '0.8rem',
                                textTransform: 'none',
                                color: colors.textPrimary,
                                bgcolor: `${currentHeaderColor}14`,
                                borderRadius: '12px',
                                px: 1.5,
                                py: 0.75,
                                minWidth: 160,
                                justifyContent: 'space-between',
                                border: `1px solid ${currentHeaderColor}26`,
                                '&:hover': {
                                    bgcolor: `${currentHeaderColor}1f`,
                                    borderColor: `${currentHeaderColor}40`,
                                },
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, ml: 0.5 }}>
                                <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: currentHeaderColor, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>
                                    Team
                                </Typography>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 }}>
                                    {user.active_team?.team_name || "Select Team"}
                                </Typography>
                            </Box>
                        </Button>

                        <Menu
                            anchorEl={teamMenuOpen}
                            open={Boolean(teamMenuOpen)}
                            onClose={() => setTeamMenuOpen(null)}
                            PaperProps={{
                                sx: {
                                    minWidth: { xs: 200, sm: 240 },
                                    maxWidth: { xs: 260, sm: 'none' },
                                    maxHeight: { xs: '60vh', sm: 'none' },
                                    mt: 1,
                                    borderRadius: { xs: '10px', sm: '12px' },
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                    border: '1px solid rgba(0,0,0,0.06)',
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': { width: '3px' },
                                    '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
                                }
                            }}
                        >
                            <Box sx={{
                                px: { xs: 1.5, sm: 2 },
                                py: { xs: 1, sm: 1.5 },
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                            }}>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Switch Team ({user.teams?.length || 0})
                                </Typography>
                            </Box>
                            <Box sx={{ py: { xs: 0.5, sm: 1 } }}>
                                {user.teams?.map((team) => {
                                    const isActive = user.active_team?.team_id === team.team_id;
                                    return (
                                        <MenuItem
                                            key={team.team_id}
                                            onClick={() => { handleTeamSwitch(team); setTeamMenuOpen(null); }}
                                            sx={{
                                                fontFamily,
                                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                py: { xs: 1, sm: 1.5 },
                                                px: { xs: 1.5, sm: 2 },
                                                mx: { xs: 0.5, sm: 1 },
                                                borderRadius: { xs: '8px', sm: '10px' },
                                                minHeight: 'auto',
                                                bgcolor: isActive ? `${currentHeaderColor}1a` : 'transparent',
                                                '&:hover': { bgcolor: isActive ? `${currentHeaderColor}26` : 'rgba(0,0,0,0.04)' },
                                            }}
                                        >
                                            <Box sx={{
                                                width: { xs: 26, sm: 32 },
                                                height: { xs: 26, sm: 32 },
                                                borderRadius: { xs: '6px', sm: '8px' },
                                                bgcolor: isActive ? currentHeaderColor : colors.textTertiary,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mr: { xs: 1, sm: 1.5 },
                                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                fontWeight: 700,
                                                color: '#fff',
                                                flexShrink: 0,
                                            }}>
                                                {team.team_name?.charAt(0)?.toUpperCase() || 'T'}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontFamily,
                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                    fontWeight: isActive ? 600 : 500,
                                                    color: isActive ? currentHeaderColor : colors.textPrimary,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {team.team_name}
                                                </Typography>
                                                {team.user_role_in_team && (
                                                    <Typography sx={{ fontFamily, fontSize: { xs: '0.6rem', sm: '0.7rem' }, color: colors.textTertiary, textTransform: 'capitalize' }}>
                                                        {team.user_role_in_team}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {isActive && (
                                                <CheckCircleIcon sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' }, color: currentHeaderColor, ml: 0.5 }} />
                                            )}
                                        </MenuItem>
                                    );
                                })}
                            </Box>
                        </Menu>
                    </Box>
                )}

                {/* Chatbot Selector - Only show on Creator Panel */}
                {activePanel === PanelType.CREATOR && (
                <Box sx={{ mr: { xs: 0, md: 1 } }}>
                    {isLoading ? (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: { xs: 1, md: 2 },
                            py: 1,
                            borderRadius: '10px',
                            bgcolor: 'rgba(0,0,0,0.04)',
                        }}>
                            <CircularProgress size={16} sx={{ color: currentHeaderColor }} />
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textSecondary, display: { xs: 'none', md: 'block' } }}>
                                Loading...
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Mobile: icon-only button */}
                            <Tooltip title={activeChatbot ? activeChatbot.chatbot_name : 'Select Chatbot'}>
                                <IconButton
                                    onClick={handleChatbotMenuOpen}
                                    sx={{
                                        display: { xs: 'flex', md: 'none' },
                                        width: 36,
                                        height: 36,
                                        borderRadius: '10px',
                                        bgcolor: `${currentHeaderColor}14`,
                                        border: `1px solid ${currentHeaderColor}26`,
                                        '&:hover': { bgcolor: `${currentHeaderColor}1f` },
                                    }}
                                >
                                    <SmartToyIcon sx={{ fontSize: '1.1rem', color: currentHeaderColor }} />
                                </IconButton>
                            </Tooltip>
                            {/* Desktop: full button */}
                            <Button
                                onClick={handleChatbotMenuOpen}
                                startIcon={<SmartToyIcon sx={{ fontSize: '1.1rem', color: currentHeaderColor }} />}
                                endIcon={<ExpandMoreIcon sx={{ fontSize: '1rem' }} />}
                                sx={{
                                    display: { xs: 'none', md: 'inline-flex' },
                                    fontFamily,
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    color: colors.textPrimary,
                                    bgcolor: `${currentHeaderColor}14`,
                                    borderRadius: '10px',
                                    px: 2,
                                    py: 0.75,
                                    minWidth: 180,
                                    justifyContent: 'space-between',
                                    border: `1px solid ${currentHeaderColor}26`,
                                    '&:hover': { bgcolor: `${currentHeaderColor}1f` },
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1, ml: 0.5 }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: currentHeaderColor, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: 1 }}>
                                        Active Bot
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 }}>
                                        {activeChatbot ? activeChatbot.chatbot_name : 'Select Chatbot'}
                                    </Typography>
                                </Box>
                            </Button>

                            <Menu
                                anchorEl={chatbotMenuAnchor}
                                open={Boolean(chatbotMenuAnchor)}
                                onClose={handleChatbotMenuClose}
                                PaperProps={{
                                    sx: {
                                        minWidth: { xs: 220, sm: 280 },
                                        maxWidth: { xs: 280, sm: 'none' },
                                        maxHeight: { xs: '60vh', sm: 400 },
                                        mt: 1,
                                        borderRadius: { xs: '10px', sm: '12px' },
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                        border: '1px solid rgba(0,0,0,0.06)',
                                        overflowY: 'auto',
                                        '&::-webkit-scrollbar': { width: '3px' },
                                        '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: '4px' },
                                    }
                                }}
                            >
                                <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 }, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Your Chatbots ({chatbots.length})
                                    </Typography>
                                </Box>

                                {chatbots.length === 0 ? (
                                    <Box sx={{ py: { xs: 2, sm: 3 }, textAlign: 'center' }}>
                                        <SmartToyIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: colors.textTertiary, mb: 1 }} />
                                        <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: colors.textSecondary }}>
                                            No chatbots found
                                        </Typography>
                                    </Box>
                                ) : (
                                    chatbots.map((bot) => (
                                        <MenuItem
                                            key={bot.id}
                                            onClick={() => handleChatbotSelect(bot.id)}
                                            selected={activeChatbot?.id === bot.id}
                                            sx={{
                                                py: { xs: 1, sm: 1.5 },
                                                minHeight: 'auto',
                                                '&.Mui-selected': {
                                                    bgcolor: `${currentHeaderColor}14`,
                                                    '&:hover': { bgcolor: `${currentHeaderColor}1f` },
                                                },
                                                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                                                <SmartToyIcon
                                                    sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: activeChatbot?.id === bot.id ? currentHeaderColor : colors.textTertiary }}
                                                />
                                            </ListItemIcon>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 500, color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {bot.chatbot_name}
                                                </Typography>
                                                {activeChatbot?.id === bot.id && (
                                                    <Chip
                                                        label="Active"
                                                        size="small"
                                                        sx={{
                                                            height: { xs: 16, sm: 18 },
                                                            mt: 0.5,
                                                            fontSize: '0.6rem',
                                                            bgcolor: `${currentHeaderColor}26`,
                                                            color: currentHeaderColor,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))
                                )}

                                <Divider sx={{ my: { xs: 0.5, sm: 1 } }} />

                                {user?.active_team?.user_role_in_team?.toLowerCase() === "admin" && (
                                    <MenuItem onClick={handleCreateNew} sx={{ py: { xs: 0.75, sm: 1.5 }, minHeight: 'auto' }}>
                                        <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                                            <AddCircleOutlineIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: currentHeaderColor }} />
                                        </ListItemIcon>
                                        <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600, color: currentHeaderColor }}>
                                            Create New Chatbot
                                        </Typography>
                                    </MenuItem>
                                )}

                                <MenuItem component={Link} to="/chatbots" onClick={handleChatbotMenuClose} sx={{ py: { xs: 0.75, sm: 1.5 }, minHeight: 'auto' }}>
                                    <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                                        <SettingsIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: colors.textTertiary }} />
                                    </ListItemIcon>
                                    <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: colors.textSecondary }}>
                                        Manage Chatbots
                                    </Typography>
                                </MenuItem>
                            </Menu>
                        </>
                    )}
                </Box>
                )}

                {/* Panel Switcher - For users with access to multiple panels */}
                <PanelSwitcher />

                {/* Reminders & Notifications */}
                <HeaderReminders panelColor={currentHeaderColor} />
                <HeaderNotifications panelColor={currentHeaderColor} />

                {/* Theme Toggle */}
                <Tooltip title={mode === 'light' ? "Dark Mode" : "Light Mode"}>
                    <IconButton
                        onClick={toggleTheme}
                        sx={{
                            color: colors.textSecondary,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                        }}
                    >
                        {mode === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
                    </IconButton>
                </Tooltip>

                {/* Profile Menu */}
                <Tooltip title="Account">
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            borderRadius: '12px',
                            p: 0.75,
                            ml: 0.5,
                            transition: 'all 0.2s ease',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                        }}
                        onClick={handleProfileClick}
                    >
                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                            <Avatar
                                alt={`${fname} ${lname}`}
                                src={profileImg}
                                sx={{
                                    width: 34,
                                    height: 34,
                                }}
                            >
                                {!profileImg && <AccountCircle />}
                            </Avatar>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: -1,
                                    right: -1,
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    bgcolor: STATUS_CONFIG[userStatus]?.color || '#9ca3af',
                                    border: '2px solid #fff',
                                }}
                            />
                        </Box>
                        {fname && (
                            <Typography sx={{
                                ml: 1,
                                mr: 0.5,
                                display: { xs: 'none', sm: 'block' },
                                fontFamily,
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: colors.textPrimary,
                            }}>
                                {fname}
                            </Typography>
                        )}
                        <ExpandMoreIcon sx={{ fontSize: '1rem', color: colors.textTertiary }} />
                    </Box>
                </Tooltip>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleProfileClose}
                    PaperProps={{
                        sx: {
                            minWidth: { xs: 200, sm: 220 },
                            maxWidth: { xs: 260, sm: 'none' },
                            maxHeight: { xs: '70vh', sm: '80vh' },
                            mt: 1,
                            borderRadius: { xs: '10px', sm: '12px' },
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(0,0,0,0.06)',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': { width: '3px' },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(0,0,0,0.15)',
                                borderRadius: '4px',
                            },
                        }
                    }}
                >
                    <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 }, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        <Typography sx={{ fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' }, fontWeight: 600, color: colors.textPrimary }}>
                            {fname} {lname}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: { xs: '0.7rem', sm: '0.75rem' }, color: colors.textTertiary, wordBreak: 'break-all' }}>
                            {user?.user_email}
                        </Typography>
                    </Box>

                    {/* Status Selector */}
                    <Box sx={{ px: { xs: 0.75, sm: 1 }, py: { xs: 0.75, sm: 1 }, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em', px: { xs: 0.5, sm: 1 }, mb: 0.5 }}>
                            Status
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, px: 0.5 }}>
                            {Object.entries(STATUS_CONFIG).map(([key, { label, color }]) => (
                                <Chip
                                    key={key}
                                    label={label}
                                    size="small"
                                    onClick={() => handleStatusChange(key)}
                                    icon={<Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, ml: '4px !important' }} />}
                                    sx={{
                                        fontFamily,
                                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                        fontWeight: userStatus === key ? 600 : 400,
                                        height: { xs: 22, sm: 26 },
                                        bgcolor: userStatus === key ? `${color}1a` : 'transparent',
                                        border: userStatus === key ? `1px solid ${color}40` : '1px solid rgba(0,0,0,0.08)',
                                        color: userStatus === key ? color : colors.textSecondary,
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: `${color}14` },
                                        '& .MuiChip-icon': { mr: 0 },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    <MenuItem onClick={handleProfileClose} component={Link} to="/user-profile" sx={{ py: { xs: 0.75, sm: 1.25 }, minHeight: 'auto' }}>
                        <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}><PersonOutlineIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} /></ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}>Profile</ListItemText>
                    </MenuItem>

                    {user?.active_team?.user_role_in_team?.toLowerCase() === "admin" && (
                        <MenuItem onClick={handleProfileClose} component={Link} to="/Billing" sx={{ py: { xs: 0.75, sm: 1.25 }, minHeight: 'auto' }}>
                            <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}><ReceiptLongIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} /></ListItemIcon>
                            <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}>Billing</ListItemText>
                        </MenuItem>
                    )}

                    <MenuItem onClick={handleProfileClose} component={Link} to="/exception-log" sx={{ py: { xs: 0.75, sm: 1.25 }, minHeight: 'auto' }}>
                        <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}><BugReportIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }} /></ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' } } }}>Exception Log</ListItemText>
                    </MenuItem>

                    <Divider sx={{ my: { xs: 0.5, sm: 1 } }} />

                    <MenuItem onClick={handleProfileClose} component="a" href="https://klonit.ai/privacy-policy" target="_blank" rel="noopener noreferrer" sx={{ py: { xs: 0.75, sm: 1.25 }, minHeight: 'auto' }}>
                        <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}><PolicyIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: colors.textTertiary }} /></ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: colors.textSecondary } }}>Privacy Policy</ListItemText>
                    </MenuItem>

                    <MenuItem onClick={handleProfileClose} component="a" href="https://klonit.ai/terms-of-use" target="_blank" rel="noopener noreferrer" sx={{ py: { xs: 0.75, sm: 1.25 }, minHeight: 'auto' }}>
                        <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}><GavelIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: colors.textTertiary }} /></ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: colors.textSecondary } }}>Terms & Conditions</ListItemText>
                    </MenuItem>

                    <Divider sx={{ my: { xs: 0.5, sm: 1 } }} />

                    <MenuItem onClick={handleProfileClose} component={Link} to="/log-out" sx={{ py: { xs: 0.75, sm: 1.25 }, minHeight: 'auto' }}>
                        <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}><LogoutIcon sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, color: '#dc2626' }} /></ListItemIcon>
                        <ListItemText primaryTypographyProps={{ sx: { fontFamily, fontSize: { xs: '0.8rem', sm: '0.875rem' }, color: '#dc2626', fontWeight: 500 } }}>Log out</ListItemText>
                    </MenuItem>
                </Menu>
            </Toolbar>
        </AppBar>
    );
}